<template>
    <layout>
        <template v-slot:header>
            <AppHead :title="`Product ${product.sku}`" />
            <h2>Product #{{ product.id }} - {{ product.sku }} - {{ product.name }}</h2>
            <flash-messages />
        </template>

        <template v-slot:default>
            <div class="row">
                <div class="col-sm-9">
                    <div class="mb-2">
                        <inertia-link class="btn-primary btn btn-ripple mr-2" :href="route('products.edit', product.id)"
                            tabindex="-1">Edit</inertia-link>
                        <jet-button class="mr-2" icon="fas fa-images" @click="manageImages()">Manage Images</jet-button>
                        <jet-button class="mr-2" @click="manageBundle()">Manage Bundle</jet-button>
                        <jet-button class="mr-2" :goesTo="route('admin.products.variation', product)">Manage
                            Variation</jet-button>
                        <jet-button class="mr-2" @click="activateAll()">Make Active for MTDs</jet-button>
                        <jet-button class="mb-2 mb-lg-0" type="mr-2"
                            :goesTo="this.route('products.copy', this.product.id)">Duplicate Product</jet-button>
                    </div>

                    <h3>Inventory Information</h3>
                    <div class="col-12 col-lg-6 px-0 mb-3">
                        <card title="Inventory Information">
                            <ul class="list-group">
                                <li class="px-2 py-1 list-group-item">
                                    Currently In Stock: <span class="text-secondary">{{
                                        productInfo.inventoryInfo.current_inventory }}</span>
                                </li>
                                <li class="px-2 py-1 list-group-item">
                                    Incoming Inventory Orders: <span class="text-secondary">{{
                                        productInfo.inventoryInfo.incoming_inventory }}</span>
                                </li>
                                <li class="px-2 py-1 list-group-item">
                                    Outgoing Pending Shipments: <span class="text-secondary">{{
                                        productInfo.inventoryInfo.outgoing_inventory }} </span>
                                </li>
                            </ul>
                        </card>
                    </div>

                    <card title="Products in this Set" class="mb-3">
                        <div class="table-responsive">
                            <ProductSetDetails :ProductId="product.id" :ProductSetParts="productInfo.ProductSetParts">
                            </ProductSetDetails>
                        </div>
                    </card>

                    <card title="Inventory Locations" class="mb-3">
                        <div class="table-responsive">
                            <InventoryLocation
                                :inventoryLocations="product.inventory_location ? [product.inventory_location] : []">
                            </InventoryLocation>
                        </div>
                    </card>

                    <card title="Pending Orders" class="mb-3">
                        <div class="table-responsive">
                            <table class="w-100 table table-sm">
                                <tr>
                                    <td>Date</td>
                                    <td>Order</td>
                                    <td>Qty</td>
                                    <td>Qty Remaining</td>
                                </tr>
                                <template v-for="orderItem in orderedPending">
                                    <tr v-if="orderItem.pending_count > 0">
                                        <td>{{ moment(orderItem.order.created_at).format('M/D/YY h:mm A') }}</td>
                                        <td>
                                            <inertia-link :href="route('admin.order.view', orderItem.order.id)">#{{
                                                orderItem.order.id }}</inertia-link> by <inertia-link
                                                :href="route('mtd.view', orderItem.user.id)">{{ orderItem.user.company_name
                                                }}</inertia-link>
                                            {{ orderItem.user.first_name }} {{ orderItem.user.last_name }}
                                            <OrderItemPaymentIntent class="ml-2" :orderItem="orderItem" />
                                        </td>
                                        <td>{{ orderItem.qty }}</td>
                                        <td>{{ orderItem.pending_count }}</td>
                                    </tr>
                                </template>
                            </table>
                        </div>
                    </card>

                    <card title="Order History">
                        <div class="table-responsive">
                            <table class="w-100 table table-sm">
                                <tr>
                                    <td>Date</td>
                                    <td>Order</td>
                                    <td>Qty</td>
                                    <td>Qty Remaining</td>
                                </tr>
                                <tr v-for="orderItem in orderHistory">
                                    <td>{{ moment(orderItem.order.created_at).format('M/D/YY h:mm A') }}</td>
                                    <td>
                                        <inertia-link :href="route('admin.order.view', orderItem.order.id)">#{{
                                            orderItem.order.id }}</inertia-link> by<inertia-link
                                            :href="route('mtd.view', orderItem.user.id)">{{ orderItem.user.company_name
                                            }}</inertia-link> {{ orderItem.user.first_name }} {{ orderItem.user.last_name }}
                                        <OrderItemPaymentIntent class="ml-2" :orderItem="orderItem" />
                                    </td>
                                    <td>{{ orderItem.qty }}</td>
                                    <td>{{ orderItem.pending_count }}</td>
                                </tr>
                            </table>
                        </div>
                    </card>

                </div>
                <div class="col-sm-3 pt-3 mt-lg-0" v-if="images.length > 0">
                    <img class="w-100 rounded" :src="images[0].url" alt="Product Image" />
                </div>
            </div>
        </template>
    </layout>
</template>

<script>
import Layout from '@/Shared/Admin/Layout'
import JetButton from '@/Jetstream/Button'
import JetFormSection from '@/Jetstream/FormSection'
import JetInput from '@/Jetstream/Input'
import JetCheckbox from '@/Jetstream/Checkbox'
import JetInputError from '@/Jetstream/InputError'
import JetLabel from '@/Jetstream/Label'
import JetActionMessage from '@/Jetstream/ActionMessage'
import InventoryLocation from './InventoryLocation.vue'
import moment from 'moment'
import OrderItemPaymentIntent from '@/Components/General/OrderItemPaymentIntent'
import ProductSetDetails from '../../../Components/Products/ProductSetDetails.vue'

export default {
    components: {
        JetActionMessage,
        JetButton,
        JetFormSection,
        JetInput,
        JetCheckbox,
        JetInputError,
        JetLabel,
        InventoryLocation,
        Layout,
        OrderItemPaymentIntent,
        ProductSetDetails,
    },
    props: {
        product: Object,
        productInfo: Object,
        images: Array,
        orderHistory: Array,
        orderedPending: Array
    },
    remember: 'form',
    data() {
        return {
            form: this.$inertia.form({
                _method: 'put',
                name: this.product.name,
                sku: this.product.sku,
                photo: null,
            }),
        }
    },
    created() {
        this.moment = moment;
    },
    methods: {
        manageImages() {
            this.$inertia.visit(this.route('products.images', this.product.id));
        },
        manageBundle() {
            this.$inertia.visit(this.route('products.bundle', this.product.id));
        },
        copy() {
            this.$inertia.visit(this.route('products.copy', this.product.id));
        },
        activateAll() {
            this.$inertia.visit(this.route('products.activatemtd', this.product.id));
        }
    },
}
</script>
